import * as React from 'react'
import styled from 'styled-components'
import I18n from 'core/i18n'
import * as constants from 'static/constants'
import { AvatarImage } from 'components/atoms'
import Memo from 'components/organisms/Tours/Weekly/Drawer/Memo'
import { tourService } from 'core/services'
import SvgIcon from 'components/atoms/SvgIcon'
import SkillContainer from 'components/molecules/Weekly/SkillContainer'
import { SoundoutOrFormalRequestModalBody } from './SoundoutOrFormalRequestModalBody'
import * as interfaces from 'static/interfaces'
import { IBuildedTour, IWeeklyTourGuide, IBuildedTourGroup } from 'utils/weeklyTypes'
import { AgentRankTag } from 'components/molecules/Weekly/AgentRankTag'
import { useWeeklyContext } from 'core/contexts/weekly/WeeklyContext'
import { getLatestConflictTour } from 'utils/weekly'
import { ConflictTourLink } from './ConflictTourLink'

declare var window: interfaces.IWindow

interface IProps {
  currentUser: any
  guide: any
  tourGuide?: IWeeklyTourGuide
  showDetail: boolean
  setGroupSelectingResource?: any
  groupSelectingResource?: any
  conflictingTourIds?: number[]
  handleAfterUpdateGuide?: (target: any, params: any) => void
  tour: IBuildedTour
  tourGroup?: IBuildedTourGroup
}

const Guide: React.VFC<IProps> = React.memo(
  ({
    currentUser,
    guide,
    tourGuide,
    showDetail,
    setGroupSelectingResource,
    groupSelectingResource,
    conflictingTourIds,
    handleAfterUpdateGuide,
    tour,
    tourGroup,
  }) => {
    const { allTours, onTourUpdate, refetchTour } = useWeeklyContext()

    const handleUpdateTourGroup = React.useCallback(
      async tourGroupId => {
        const updateTourGroup = async () => {
          const { tour_group: updatedTourGroup } = tourGroupId
            ? await tourService.assignGuide(tourGroupId, guide.id)
            : await tourService.unassignGuide(tourGuide.tour_group_id)
          refetchTour(tour.id)
        }
        const otaReservation = tour.ota_reservations.find(
          _otaReservation => _otaReservation.jwt_tour_group_id === tourGuide.tour_group_id
        )
        const notNeedAlert =
          tourGroupId !== null || otaReservation?.connection?.status !== 'connected'
        try {
          if (notNeedAlert) {
            await updateTourGroup()
            return
          }
          if (confirm('本当に解除しますか？（解除メールが送信されます）')) {
            await updateTourGroup()
          }
        } catch (e) {
          console.error(e)
        }
      },
      [tour.ota_reservations, guide, tourGuide, refetchTour]
    )

    const assignStatus = React.useMemo(() => {
      return tourGuide?.tour_group_id ? 'assigned' : tourGuide?.request_status || 'nothing'
    }, [tourGuide])

    const latestConflictingTour = React.useMemo(
      () => getLatestConflictTour(tour.id, guide.id, conflictingTourIds, allTours),
      [tour.id, guide.id, conflictingTourIds, allTours]
    )

    const showMessageModal = () => {
      window.globalModal.showModal({
        small: true,
        body: (
          <SoundoutOrFormalRequestModalBody
            agent={currentUser}
            guide={guide}
            tour={tour}
            messageType={'sound_out'}
            onTourUpdate={onTourUpdate}
            latestConflictingTour={latestConflictingTour}
          />
        ),
      })
    }

    return (
      <Wrapper className={showDetail && 'card'}>
        <div className="guideInner">
          <div className="guideInnerHead">
            <div className="guideInnerHeadTop">
              <a href={`/agent/guides/${guide.id}/edit`} target="_blank" rel="noopener">
                <AvatarImage size={32} avatarURL={guide.avatar_url} />
                <div className="userName">
                  {guide.fullname || '本名未設定'}（{guide.username}）
                </div>
                {guide.agent_rank_as_guide && (
                  <AgentRankTag
                    agentRank={guide.agent_rank_as_guide}
                    guideFee={tourGroup?.paymentDetail?.guide_fee || 0}
                  />
                )}
                <SvgIcon
                  name="chevron_right"
                  className="icon"
                  ariaHidden={true}
                  role="presentation"
                />
              </a>
              {latestConflictingTour && (
                <ConflictTourLink conflictTour={latestConflictingTour} small={true} />
              )}
            </div>
            {showDetail && (
              <>
                <div className="guideInfo">
                  <span>{guide.age !== 0 && String(guide.age).replace(/.$/, '0代')}</span>
                  <span>{I18n.t(`enums.user.gender.${guide.gender}`, { locale: 'ja' })}</span>
                  <span>催行ツアー数 {guide.tour_guide_count}</span>
                </div>
              </>
            )}
          </div>
          {showDetail && (
            <>
              {guide.assigned_skills.length > 0 && (
                <SkillContainer type="skill" skills={guide.assigned_skills} />
              )}
              {guide.assign_guide_tour_skills.length > 0 && (
                <SkillContainer type="tour_skill" skills={guide.assign_guide_tour_skills} />
              )}
              {guide.guide_range_tags.length > 0 && (
                <div className="rangeTagContainer">
                  {guide.guide_range_tags.map(GuideRangeTag => (
                    <div className="rangeTag" key={GuideRangeTag.id} aria-label="temporary tag">
                      <SvgIcon name="note" className="icon" ariaHidden={true} role="presentation" />
                      {GuideRangeTag.range_tag.name}
                      {(GuideRangeTag.start_date || GuideRangeTag.end_date) &&
                        `(${GuideRangeTag.start_date || ''}~${GuideRangeTag.end_date || ''})`}
                    </div>
                  ))}
                </div>
              )}
              <Memo
                target={guide}
                targetClass="user"
                external={false}
                internal={true}
                handleAfterUpdate={handleAfterUpdateGuide}
              />
            </>
          )}
        </div>
        {showDetail && (
          <>
            <div className="action">
              {assignStatus === 'assigned' && (
                <div
                  onClick={() => handleUpdateTourGroup(null)}
                  role="button"
                  className={assignStatus}
                  aria-label="Unassign this guide"
                >
                  <SvgIcon
                    name="remove_circle"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  アサイン解除
                </div>
              )}
              {assignStatus === 'agent_official_requested' &&
                !tourGuide.agent_rank_id &&
                (groupSelectingResource === `tour_guide_${tourGuide.id}` ? (
                  <div
                    onClick={() => setGroupSelectingResource(null)}
                    role="button"
                    className="cancel"
                    aria-label="Cancel"
                  >
                    <SvgIcon
                      name="close_circle"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    キャンセル
                  </div>
                ) : (
                  <div
                    onClick={() => setGroupSelectingResource(`tour_guide_${tourGuide.id}`)}
                    role="button"
                    className={assignStatus}
                    aria-label="Assign this guide"
                  >
                    <SvgIcon
                      name="add_circle"
                      className="icon"
                      ariaHidden={true}
                      role="presentation"
                    />
                    アサイン
                  </div>
                ))}
              {assignStatus !== 'agent_official_requested' && assignStatus !== 'assigned' && (
                <div
                  onClick={showMessageModal}
                  className={assignStatus}
                  aria-label="Sound this guide"
                >
                  <SvgIcon
                    name="more_circle"
                    className="icon"
                    ariaHidden={true}
                    role="presentation"
                  />
                  打診
                </div>
              )}
            </div>
          </>
        )}
      </Wrapper>
    )
  }
)

const Wrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  pointer-events: auto;
  &.card {
    padding: 12px 16px;
    margin-bottom: 12px;
    background-color: ${constants.COLORS.white};
    border: 1px solid ${constants.COLORS.divider2};
    border-radius: 12px;
    .guideInner {
      gap: 8px;
    }
  }
  .guideInner {
    display: flex;
    flex-direction: column;
    font-size: 14px;
    padding-right: 8px;
    gap: 4px;
    flex: 1;
    min-width: 0;
    .guideInnerHead {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
    }
    .guideInnerHeadTop {
      width: 100%;
      display: flex;
      align-items: center;
      justify-content: space-between;

      > a {
        display: flex;
        align-items: center;
        color: ${constants.COLORS.black};
        transition: color 0.2s;
        &:hover {
          color: ${constants.COLORS.primary};
        }
        .userName {
          line-height: 16px;
          word-break: break-all;
          & + div:has(.tooltipContent) {
            margin-left: 4px;
          }
        }
        > div:first-child {
          margin-right: 8px;
        }
        .icon {
          flex: 0 0 auto;
        }
      }
    }
    .guideInfo {
      display: flex;
      align-items: center;
      color: ${constants.COLORS.subText};
      font-size: 12px;
      gap: 4px 8px;
      span {
        &:last-child {
          margin-right: 0;
        }
      }
    }
    .rangeTagContainer {
      display: flex;
      align-items: center;
      flex-wrap: wrap;
      gap: 4px;
      div {
        line-height: 16px;
        padding: 4px 8px 4px 4px;
        color: ${constants.COLORS.black};
        font-size: 11px;
        border-radius: 4px;
        word-break: break-all;
        .icon {
          width: 16px;
          height: 16px;
          margin-right: 4px;
        }
      }
    }
    .rangeTagContainer {
      .rangeTag {
        background-color: ${constants.COLORS.background2};
        .icon {
          color: ${constants.COLORS.subText};
        }
      }
    }
    .memo.agentOnly {
      margin-top: 4px;
    }
  }
  .action {
    flex: 0 0 auto;
    div,
    a {
      display: flex;
      align-items: center;
      line-height: 16px;
      padding: 8px 4px;
      color: ${constants.COLORS.white};
      font-size: 11px;
      background-color: ${constants.COLORS.primary};
      border: 1px solid ${constants.COLORS.primary};
      border-radius: 4px;
      transition: background-color 0.2s, color 0.2s, border-color 0.2s;
      cursor: pointer;
      .icon {
        width: 16px;
        height: 16px;
        margin-right: 4px;
      }
      &:hover {
        background-color: ${constants.COLORS.secondary};
        border-color: ${constants.COLORS.secondary};
      }
      &.cancel {
        color: ${constants.COLORS.subText};
        background-color: ${constants.COLORS.background2};
        border-color: ${constants.COLORS.divider};
        &:hover {
          background-color: ${constants.COLORS.background};
          border-color: ${constants.COLORS.subText};
        }
      }
      &.nothing,
      &.soundouted,
      &.agent_declined,
      &.guide_declined {
        color: ${constants.COLORS.info2};
        background-color: ${constants.COLORS.white};
        border-color: ${constants.COLORS.info2};
        &:hover {
          color: ${constants.COLORS.white};
          background-color: ${constants.COLORS.info2};
        }
      }
      &.assigned {
        color: ${constants.COLORS.danger};
        background-color: ${constants.COLORS.dangerBG};
        border-color: ${constants.COLORS.danger};
        &:hover {
          color: ${constants.COLORS.white};
          background-color: ${constants.COLORS.danger};
        }
      }
    }
  }
`

export default Guide
